/**
 * Cliente API de ingresos varios — vecino-facing + tesorero-facing.
 *
 * Sprint 14 S14-Otros (vecino) + Sprint 17 Fase 2 (tesorero).
 */
import { apiFetch } from './client';

// === Vecino: vista reducida ===

export type EstadoIngresoVecino = 'aprobado' | 'pendiente_revisar';

export interface IngresoVarioVecinoRead {
  id: string;
  fecha: string;
  descripcion: string;
  monto: number;
  estado: EstadoIngresoVecino;
  categoria_nombre: string;
}

export function listarIngresosVariosDeLote(
  loteId: string,
): Promise<IngresoVarioVecinoRead[]> {
  return apiFetch<IngresoVarioVecinoRead[]>(
    `/lotes/${loteId}/ingresos-varios`,
  );
}

// === Tesorero: vista completa ===

export type EstadoIngreso =
  | 'aprobado'
  | 'pendiente_revisar'
  | 'por_cobrar'
  | 'cobrado'
  | 'anulado';

// El barrio opera solo por transferencias (nunca efectivo). `whatsapp` =
// avisado/comprobante por WhatsApp de una transferencia.
export type OrigenPago = 'whatsapp' | 'transferencia_manual' | 'otro';

export interface IngresoVarioRead {
  id: string;
  lote_id: string | null;
  persona_id: string | null;
  categoria_id: string;
  proyecto_id: string | null;
  pago_id: string | null;
  distribucion_pago_id: string | null;
  monto: number;
  fecha: string;
  descripcion: string;
  comprobante_url: string | null;
  origen_pago: OrigenPago | null;
  estado: EstadoIngreso;
  registrado_por_usuario_id: string | null;
  motivo_anulacion: string | null;
  anulado_at: string | null;
  anulado_por_usuario_id: string | null;
  creado_at: string;
  actualizado_at: string;
}

export interface IngresoVarioResumen {
  /** Plata del barrio recibida (aprobado + cobrado), SIN fondos de terceros. */
  recibido: number;
  /** Fondos de terceros recibidos (pass-through muni/JJVV), mostrados aparte. */
  terceros: number;
  /** Facturado pero aún no pagado (estado por_cobrar). */
  por_cobrar: number;
}

export interface IngresoVarioCreateInput {
  lote_id?: string | null;
  categoria_id: string;
  monto: number;
  fecha: string;
  descripcion: string;
  origen_pago?: OrigenPago | null;
  estado?: 'aprobado' | 'pendiente_revisar';
  comprobante_url?: string | null;
}

export function listIngresosVarios(filtros: {
  fecha_desde?: string;
  fecha_hasta?: string;
  incluir_anulados?: boolean;
  lote_id?: string;
  estado?: EstadoIngreso;
} = {}): Promise<IngresoVarioRead[]> {
  const params = new URLSearchParams();
  if (filtros.fecha_desde) params.set('fecha_desde', filtros.fecha_desde);
  if (filtros.fecha_hasta) params.set('fecha_hasta', filtros.fecha_hasta);
  if (filtros.incluir_anulados)
    params.set('incluir_anulados', String(filtros.incluir_anulados));
  if (filtros.lote_id) params.set('lote_id', filtros.lote_id);
  if (filtros.estado) params.set('estado', filtros.estado);
  const qs = params.toString();
  return apiFetch<IngresoVarioRead[]>(
    `/ingresos-varios/${qs ? `?${qs}` : ''}`,
  );
}

export function crearIngresoVario(
  data: IngresoVarioCreateInput,
): Promise<IngresoVarioRead> {
  return apiFetch<IngresoVarioRead>('/ingresos-varios/', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export function anularIngresoVario(
  id: string,
  motivo: string,
): Promise<IngresoVarioRead> {
  return apiFetch<IngresoVarioRead>(`/ingresos-varios/${id}/anular`, {
    method: 'POST',
    body: JSON.stringify({ motivo }),
  });
}

/**
 * KPIs de la pantalla calculados en backend (misma fuente de verdad que el
 * dashboard: exclusión de fondos de terceros). Separa lo recibido de lo
 * por cobrar.
 */
export function getResumenIngresosVarios(): Promise<IngresoVarioResumen> {
  return apiFetch<IngresoVarioResumen>('/ingresos-varios/resumen');
}
